import type { ContentAST } from './astTypes'
import { getBlockText } from './astUtils'

/**
 * Identity of a file's heading structure — id, level and text of every heading.
 *
 * The editor refreshes several book-wide views after a save (the `^` sections
 * TOC, tag usage counts, the outline tag subtree, server-side
 * chapterSectionKey reconciliation), and each of those endpoints rescans every
 * source file in the book. All of them are derived from exactly this, so an
 * unchanged signature means none of them can have moved and the refresh can be
 * skipped — which is nearly every autosave, since ordinary prose editing does
 * not touch headings.
 *
 * Deliberately *all* headings, not only `^`-marked ones: the sections TOC shows
 * each `^` section under its ancestor headings, so renaming a plain heading
 * changes a book-wide view too. Level is included because it decides which
 * headings are a section's ancestors, and blockId because the TOC and
 * publishedSections are keyed by it.
 */
export function headingSignature(ast: ContentAST | null | undefined): string {
  const rows: Array<[string, number, string]> = []
  for (const block of ast?.blocks ?? []) {
    if (block.type === 'heading') {
      rows.push([block.id, block.level, getBlockText(block)])
    }
  }
  // JSON rather than a joined string: it escapes control characters, so no
  // heading text can forge a field or record boundary.
  return JSON.stringify(rows)
}
